import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import adaptivePlugin from '@fullcalendar/adaptive';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FULLCALENDAR_LICENSE_KEY } from '../../../config/fullcalendar.config';
import { HolidayDto } from '../../../models/holiday.models';
import {
  CreateTimeOffRequestRequest,
  TimeOffApiMode,
  TimeOffBalanceDto,
  TimeOffEntry,
  TimeOffHistoryDto,
  TimeOffOverlapCheckResultDto,
  TimeOffResourcePolicyDto,
  TimeOffTypeBalanceDto,
  TimeOffTypeCodes,
  TimeOffTypeHistoryDto,
  TimeOffTypeLookup,
} from '../../../models/time-off.models';
import { AuthService } from '../../../services/auth.service';
import { NotesService } from '../../../services/notes.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { TimeOffService } from '../../../services/time-off.service';
import { balanceValueClass } from '../../../utils/api-error.utils';
import { overlapConfirmationSection } from '../../../utils/time-off-overlap.utils';
// Aliased: the component method below is also called printCalendar, and an
// unqualified call to a shadowed import reads as recursion. Any refactor that
// qualifies member access with `this.` would then turn it into real recursion
// that still compiles.
import { printCalendar as openPrintDialog } from '../../../utils/calendar-print.utils';
import {
  createTimeOffEventClickHandler,
  mapHolidaysToEventInputs,
  mapTimeOffEntriesToEventInputs,
  timeOffEventDidMount,
  timeOffEventMouseEnter,
  timeOffEventMouseLeave,
} from '../../../utils/time-off-calendar.utils';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import {
  TimeOffBalanceInfoDialogComponent,
  TimeOffBalanceInfoDialogData,
} from '../time-off-balance-info-dialog/time-off-balance-info-dialog.component';
import {
  TimeOffDetailsDialogComponent,
  TimeOffDetailsDialogData,
  TimeOffDetailsDialogResult,
} from '../time-off-details-dialog/time-off-details-dialog.component';
import {
  TimeOffNotesDialogComponent,
  TimeOffNotesDialogData,
} from '../time-off-notes-dialog/time-off-notes-dialog.component';
import {
  TimeOffPolicyEditDialogComponent,
  TimeOffPolicyEditDialogData,
  TimeOffPolicyEditDialogResult,
} from '../time-off-policy-edit-dialog/time-off-policy-edit-dialog.component';
import {
  UnsavedChangesDialogComponent,
  UnsavedChangesDialogData,
} from '../unsaved-changes-dialog/unsaved-changes-dialog.component';

/**
 * One segment of a balance footnote sentence; `value` is set for the numeric
 * segments so the template can style them.
 */
export interface BalanceFootnotePart {
  text: string;
  value: number | null;
  bold?: boolean;
}

/**
 * A balance footnote: the explanation split into sentences (each a list of
 * parts) so the modal can render one line per sentence while keeping the styled
 * number spans.
 */
export interface BalanceFootnote {
  typeCode: string;
  marker: string;
  sentences: BalanceFootnotePart[][];
}

/**
 * How many events a day cell shows before collapsing the rest behind "+N more".
 * Named because printing has to lift the collapse and then restore exactly this
 * value afterwards (PBI 6471) — a printout that hides entries is incomplete.
 */
const DEFAULT_DAY_MAX_EVENTS = 2;

@Component({
  selector: 'app-time-off-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    MatButtonToggleModule,
    FullCalendarModule,
  ],
  templateUrl: './time-off-management.component.html',
  styleUrls: ['./time-off-management.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, marginBottom: 0, overflow: 'hidden' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1, marginBottom: '*' }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0, marginBottom: 0 }),
        ),
      ]),
    ]),
  ],
})
export class TimeOffManagementComponent
  implements OnInit, OnChanges, OnDestroy
{
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private timeOffService = inject(TimeOffService);
  private notesService = inject(NotesService);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);

  readonly entries = input<TimeOffEntry[]>([]);
  readonly lookupTypes = input<TimeOffTypeLookup[]>([]);
  readonly sortedLookupTypes = computed(() =>
    [...this.lookupTypes()].sort((a, b) => a.sortOrder - b.sortOrder),
  );
  readonly isReadOnly = input<boolean>(false);
  readonly canEditEntitledDays = input<boolean>(false);
  readonly apiMode = input<TimeOffApiMode>('admin');
  readonly employeeId = input<string>('');
  readonly employeeName = input<string>('');
  readonly classYear = input<string | undefined>(undefined);
  readonly seniority = input<string | undefined>(undefined);
  readonly canApprove = input<boolean>(false);

  readonly entriesSaved = output<void>();
  readonly entitledDaysSaved = output<void>();
  readonly editModeRequested = output<void>();
  readonly requestStatusChanged = output<void>();

  displayedColumns: string[] = ['start', 'end', 'type', 'daysOff', 'status'];
  isEditMode = false;

  // Pagination state (only applied when entry count exceeds pageSize)
  readonly paginationThreshold = 5;
  pageSize = 5;
  pageIndex = 0;
  readonly pageSizeOptions = [5, 10, 25];
  viewMode: 'table' | 'calendar' | 'balance' = 'calendar';
  balance: TimeOffBalanceDto | null = null;
  balanceLoading = false;
  balanceError: string | null = null;

  // Read-only history shown when a past year is selected (finalized snapshots
  // from the history endpoint; no carryover breakdown, no editing).
  history: TimeOffHistoryDto | null = null;
  historyLoading = false;
  historyError: string | null = null;

  // Balance year navigation. Defaults to the current year; users can page back
  // one year to the read-only history view and forward one year to a projected
  // sheet: the API builds next year's balance from this year's live
  // figures until the Jan 1 job materialises it. The bounds are read on every
  // use, as `currentListYear` is, so a tab left open over New Year keeps the
  // right range.
  selectedBalanceYear = new Date().getFullYear();

  /**
   * The server's current year, from the last balance it returned, and the
   * browser year it arrived in. Around New Year the two clocks disagree for a
   * few hours in either direction: a browser west of UTC still reads the old
   * year while the server has moved on, and one east of UTC is already on the
   * new year while the server is not. The server's word is the one the API
   * will accept, so it wins at the moment it arrives.
   */
  private serverCurrentYear: number | null = null;
  private browserYearAtServerReply = 0;

  /**
   * The year the balance navigation is bounded on. Until the server has
   * answered it is the browser's year. After that it is the server's year,
   * advanced by however many year boundaries the browser has crossed since
   * the reply: a tab left open past midnight on New Year's Eve must keep
   * following the clock without a round trip, but a browser that was merely
   * ahead of UTC when the reply landed must not push the bound past what the
   * server accepts.
   */
  private get effectiveCurrentYear(): number {
    if (this.serverCurrentYear === null) return this.currentListYear;
    return (
      this.serverCurrentYear +
      (this.currentListYear - this.browserYearAtServerReply)
    );
  }

  private get maxBalanceYear(): number {
    return this.effectiveCurrentYear + 1;
  }

  private get minBalanceYear(): number {
    return this.effectiveCurrentYear - 1;
  }

  // List view paging (PBI 6682). 'upcoming' is the default page: every request
  // that has not ended yet, whatever its year. A number is one year of ended
  // requests, reached by paging back from Upcoming through the current year.
  // It is a client-side filter over `entries()`, which the hosts load in full,
  // so the earliest reachable year comes from the data.
  listPage: 'upcoming' | number = 'upcoming';

  /**
   * The list page's entries and earliest reachable year, memoised on what they depend on: the
   * entries, the page and the calendar day. The template reads both several times per
   * change-detection pass, and this keeps that to one pass over the entries per change.
   */
  private listViewCache: {
    entries: TimeOffEntry[];
    page: 'upcoming' | number;
    today: string;
    filtered: TimeOffEntry[];
    earliest: number;
  } | null = null;

  // Inline add row state
  isAddingEntry = false;
  newEntryForm: FormGroup | null = null;
  isNewEntrySaving = false;
  /**
   * The add-row save under way, from the first click until it settles. A second Save, or an
   * unsaved-changes prompt arriving mid-save, joins it instead of starting another.
   */
  private newEntrySaveInFlight: Promise<boolean> | null = null;

  // Balance edit state
  readonly TimeOffTypeCodes = TimeOffTypeCodes;
  resourcePolicies: TimeOffResourcePolicyDto[] = [];

  calendarOptions: CalendarOptions = {
    // adaptivePlugin is FullCalendar Premium: it supplies the print-optimised
    // rendering the print button relies on. It also activates the license
    // check, so schedulerLicenseKey has to be set alongside it.
    plugins: [dayGridPlugin, adaptivePlugin],
    schedulerLicenseKey: FULLCALENDAR_LICENSE_KEY,
    initialView: 'dayGridMonth',
    height: 'auto',
    dayMaxEvents: DEFAULT_DAY_MAX_EVENTS,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: '',
    },
    events: [],
    eventOrder: 'order,start,-duration,allDay,title',
    eventDidMount: timeOffEventDidMount,
    eventMouseEnter: timeOffEventMouseEnter,
    eventMouseLeave: timeOffEventMouseLeave,
    // Runs after the toolbar renders; swaps FullCalendar's native (dark)
    // title tooltips on the prev/next buttons for the app's light CSS tooltip.
    datesSet: () => this.styleCalendarNavTooltips(),
  };

  pendingNotes = new Map<number, string>();

  /**
   * Stamped on the print-only caption. Refreshed as the print starts rather than
   * held from component init, so a tab left open overnight does not print
   * yesterday's date.
   */
  printedOn = new Date();

  /**
   * Only present while the calendar view is selected, since the template drops
   * the calendar entirely in the table and balance views. Hence the optional
   * access in {@link printCalendar}.
   *
   * Scoped by template ref rather than by component type: a bare
   * `viewChild(FullCalendarComponent)` matches whichever calendar renders
   * first, so a second `<full-calendar>` added to this template would silently
   * redirect Print at the wrong one with no compile error. Same reasoning as
   * the view queries in `resources.component.ts`.
   */
  private readonly calendarComponent =
    viewChild<FullCalendarComponent>('timeOffCalendar');

  private nextId = 1;
  private destroy$ = new Subject<void>();
  private holidays: HolidayDto[] = [];

  ngOnInit(): void {
    this.calendarOptions = {
      ...this.calendarOptions,
      eventClick: createTimeOffEventClickHandler(() => this.dialog),
    };
    this.buildCalendarEvents();
    this.timeOffService.getHolidays().subscribe({
      next: (holidays) => {
        this.holidays = holidays;
        this.buildCalendarEvents();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entries'] && !changes['entries'].firstChange) {
      // If we're not in edit mode, just update the view data
      if (!this.isEditMode) {
        // Reset nextId based on current entries
        if (this.entries().length > 0) {
          this.nextId = Math.max(...this.entries().map((e) => e.id)) + 1;
        }
      }
      // Keep the list page inside the years the new entries reach, so a reload
      // that drops the oldest requests cannot strand the list on an
      // unreachable year.
      if (
        this.listPage !== 'upcoming' &&
        this.listPage < this.earliestListYear
      ) {
        this.listPage = this.earliestListYear;
      }
      // Clamp pageIndex if entries shrank below current page
      const maxPageIndex = Math.max(
        0,
        Math.ceil(this.filteredEntries.length / this.pageSize) - 1,
      );
      if (this.pageIndex > maxPageIndex) {
        this.pageIndex = maxPageIndex;
      }
      // Rebuild calendar events when entries change
      this.buildCalendarEvents();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Read-only view helpers ---

  /**
   * The entries on the selected list page, in date order (earliest start
   * first). Upcoming and past-year pages sort the same way.
   */
  get filteredEntries(): TimeOffEntry[] {
    return this.listView.filtered;
  }

  get showPaginator(): boolean {
    return this.filteredEntries.length > this.paginationThreshold;
  }

  get pagedEntries(): TimeOffEntry[] {
    const entries = this.filteredEntries;
    if (entries.length <= this.paginationThreshold) {
      return entries;
    }
    const start = this.pageIndex * this.pageSize;
    return entries.slice(start, start + this.pageSize);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  /**
   * True once the request has ended. Past rows keep the grey background on
   * every page; a past-year page is nothing but past rows.
   */
  isPastEntry(entry: TimeOffEntry): boolean {
    return this.hasEnded(entry, this.toDateKey(new Date()));
  }

  /**
   * True when the request ended before `todayKey`. Both are yyyy-MM-dd keys, which compare
   * correctly as strings, so no Date is built per entry.
   */
  private hasEnded(entry: TimeOffEntry, todayKey: string): boolean {
    const end = entry.displayEndDate;
    return !!end && end.slice(0, 10) < todayKey;
  }

  // --- List paging (PBI 6682) ---

  /**
   * Read from the clock on every use, as `printedOn` is: a year captured when the component is
   * built goes stale in a tab left open over New Year, and the new year's ended requests would
   * then sit on a page the arrows cannot reach.
   */
  get currentListYear(): number {
    return new Date().getFullYear();
  }

  /** True on the Upcoming page: every request that has not ended yet. */
  get isViewingUpcoming(): boolean {
    return this.listPage === 'upcoming';
  }

  /**
   * One pass over the entries per (entries, page, calendar day), however often the template
   * reads the result. Sorted by start date: Upcoming and past-year pages share one order.
   */
  private get listView(): { filtered: TimeOffEntry[]; earliest: number } {
    const entries = this.entries();
    const page = this.listPage;
    const today = this.toDateKey(new Date());
    const cached = this.listViewCache;
    if (
      cached &&
      cached.entries === entries &&
      cached.page === page &&
      cached.today === today
    ) {
      return cached;
    }

    let earliest = Number(today.slice(0, 4));
    const filtered: TimeOffEntry[] = [];
    for (const entry of entries) {
      const ended = this.hasEnded(entry, today);
      if (ended) {
        const year = this.startYear(entry);
        if (!Number.isNaN(year) && year < earliest) earliest = year;
      }
      if (this.isOnListPage(entry, page, ended)) filtered.push(entry);
    }
    // yyyy-MM-dd keys sort correctly as plain strings.
    const startOf = (entry: TimeOffEntry): string =>
      (entry.displayStartDate ?? '').slice(0, 10);
    filtered.sort((a, b) => {
      const x = startOf(a);
      const y = startOf(b);
      return x < y ? -1 : x > y ? 1 : 0;
    });

    this.listViewCache = { entries, page, today, filtered, earliest };
    return this.listViewCache;
  }

  /**
   * The earliest year the list can page back to: the year the oldest ended
   * request starts in, never later than the current year. Years in between
   * with no requests stay reachable, so the arrows behave predictably.
   */
  get earliestListYear(): number {
    return this.listView.earliest;
  }

  canGoToPreviousListYear(): boolean {
    return (
      this.listPage === 'upcoming' || this.listPage > this.earliestListYear
    );
  }

  canGoToNextListYear(): boolean {
    return !this.isViewingUpcoming;
  }

  /** Upcoming steps to this year's past, then one year back at a time. */
  goToPreviousListYear(): void {
    if (!this.canGoToPreviousListYear()) return;
    this.setListPage(
      this.listPage === 'upcoming' ? this.currentListYear : this.listPage - 1,
    );
  }

  goToNextListYear(): void {
    if (this.listPage === 'upcoming') return;
    this.setListPage(
      this.listPage >= this.currentListYear ? 'upcoming' : this.listPage + 1,
    );
  }

  goToCurrentListYear(): void {
    this.setListPage('upcoming');
  }

  /**
   * The arrows of the shared year-nav template, which the list and balance
   * views both render: one handler routes a step to the right view's paging.
   */
  stepYearNav(kind: 'list' | 'balance', step: -1 | 1): void {
    if (kind === 'list') {
      if (step < 0) this.goToPreviousListYear();
      else this.goToNextListYear();
    } else if (step < 0) {
      this.goToPreviousBalanceYear();
    } else {
      this.goToNextBalanceYear();
    }
  }

  /** Small caption over the title, as on the capacity card's period nav. */
  get listYearCaption(): string {
    return this.isViewingUpcoming ? 'Current' : 'Past';
  }

  get listYearLabel(): string {
    return this.isViewingUpcoming ? 'Upcoming' : `${this.listPage}`;
  }

  /**
   * Every page change lands on the first page: a page index reached in a busy
   * year would run past the end of a quiet one.
   */
  private setListPage(page: 'upcoming' | number): void {
    this.listPage = page;
    this.pageIndex = 0;
  }

  /**
   * Page membership. Upcoming holds every request that has not ended. A year
   * page holds every ended request whose span touches that year, so a request
   * over New Year is on both years' pages.
   */
  private isOnListPage(
    entry: TimeOffEntry,
    page: 'upcoming' | number,
    ended: boolean,
  ): boolean {
    if (page === 'upcoming') return !ended;
    if (!ended) return false;
    return this.startYear(entry) <= page && this.endYear(entry) >= page;
  }

  /**
   * Year of a yyyy-MM-dd key, NaN when there is none. Sliced rather than
   * parsed so a date never shifts a day across a timezone boundary.
   */
  private yearOf(dateKey: string | null | undefined): number {
    return Number((dateKey ?? '').slice(0, 4)) || NaN;
  }

  private startYear(entry: TimeOffEntry): number {
    return this.yearOf(entry.displayStartDate);
  }

  private endYear(entry: TimeOffEntry): number {
    const end = this.yearOf(entry.displayEndDate);
    return Number.isNaN(end) ? this.startYear(entry) : end;
  }

  openDetailsDialog(entry: TimeOffEntry): void {
    const dialogData: TimeOffDetailsDialogData = {
      timeOffRequestId: entry.id,
      employeeId: this.employeeId(),
      employeeName: this.employeeName(),
      statusCode: entry.statusCode || '',
      canApprove: this.canApprove(),
      // Day editing follows the Write permission the API checks, not the approval right.
      canEditDays: this.authService.canSetResourceTimeOff(),
      apiMode: this.apiMode(),
      // Self-service always shows the signed-in user's own requests; the admin view of a
      // resource page can be your own page too.
      isOwnRequest:
        this.apiMode() === 'self-service' ||
        this.authService.isCurrentEmployee(this.employeeId()),
      hasNotes: entry.hasNotes,
      hasManagerNotes: entry.hasManagerNotes,
      typeName: this.getTypeName(entry),
      days: entry.days,
      carryOverDays: entry.carryOverDays,
      otherRequestDays: this.otherRequestDaysFor(entry),
      holidays: this.holidays,
      startDate: entry.displayStartDate,
      endDate: entry.displayEndDate,
      daysOff: entry.daysOff,
    };

    const dialogRef = this.dialog.open(TimeOffDetailsDialogComponent, {
      width: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: TimeOffDetailsDialogResult | null) => {
        if (!result) return;
        if (result.action === 'approve') {
          this.approveEntry(entry, result.note);
        } else if (result.action === 'return') {
          this.denyEntry(entry, result.note);
        } else if (result.action === 'cancel') {
          this.cancelEntry(entry, result.note);
        } else if (result.action === 'edit') {
          this.onEntryDaysUpdated();
        }
      });
  }

  /** The dialog has already saved the new day set; this side reports it and refreshes. */
  private onEntryDaysUpdated(): void {
    this.snackbarService.showSuccess('Time off request updated.');
    this.requestStatusChanged.emit();
    this.refreshBalanceAfterChange();
  }

  /**
   * Days held by the employee's other pending or approved requests, so the edit picker
   * cannot double-book them. A missing status means approved, as elsewhere in this component.
   */
  private otherRequestDaysFor(entry: TimeOffEntry): string[] {
    return this.entries()
      .filter((other) => {
        if (other.id === entry.id) return false;
        const code = (other.statusCode || 'APPROVED').toUpperCase();
        return code === 'APPROVED' || code === 'PENDING';
      })
      .flatMap((other) => other.days ?? []);
  }

  private openPendingNotesDialog(tempId: number): void {
    const existingNotes = this.pendingNotes.get(tempId);
    const dialogData: TimeOffNotesDialogData = {
      timeOffRequestId: tempId,
      pendingMode: true,
      dialogTitle: 'Add Notes',
      employeeName: this.employeeName(),
      apiMode: this.apiMode() === 'admin' ? 'admin' : 'self',
      notesInfo: existingNotes ? { notes: existingNotes, mentions: [] } : null,
    };

    const dialogRef = this.dialog.open(TimeOffNotesDialogComponent, {
      width: '900px',
      maxHeight: '80vh',
      autoFocus: false,
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { notes: string } | undefined | null) => {
        if (result?.notes !== undefined) {
          if (result.notes.trim()) {
            this.pendingNotes.set(tempId, result.notes);
          } else {
            this.pendingNotes.delete(tempId);
          }
        }
      });
  }

  getTypeName(entry: TimeOffEntry): string {
    if (entry.type) return entry.type;
    if (entry.typeLookupValueId) {
      const lookup = this.lookupTypes().find(
        (t) => t.id === entry.typeLookupValueId,
      );
      return lookup?.name || 'Unknown';
    }
    return 'Unknown';
  }

  getStatusLabel(entry: TimeOffEntry): string {
    const code = (entry.statusCode || 'APPROVED').toUpperCase();
    if (code === 'DENIED') {
      return 'Returned';
    }
    if (code === 'CANCELLED' || code === 'CANCELED') {
      return 'Cancelled';
    }
    return code.charAt(0) + code.slice(1).toLowerCase();
  }

  getStatusBadgeClass(entry: TimeOffEntry): string {
    const code = (entry.statusCode || 'APPROVED').toUpperCase();
    switch (code) {
      case 'PENDING':
        return 'badge-pending';
      case 'DENIED':
        return 'badge-denied';
      case 'CANCELLED':
      case 'CANCELED':
        return 'badge-canceled';
      case 'APPROVED':
      default:
        return 'badge-approved';
    }
  }

  formatDateRange(entry: TimeOffEntry): string {
    const start = this.formatDisplayDate(entry.displayStartDate);
    const end = this.formatDisplayDate(entry.displayEndDate);
    if (start && end) {
      return `${start} - ${end}`;
    }
    return start || end || 'Not set';
  }

  formatDisplayDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  // --- Edit mode ---

  /**
   * Called by parent to actually enter edit mode after coordination.
   * No longer used for balance view (inline editing), but kept for parent compatibility.
   */
  enterEditMode(): void {
    this.isEditMode = true;
  }

  /**
   * Try to exit edit mode, showing dialog if there are unsaved changes.
   * Returns a promise that resolves to true if exited, false if user cancelled.
   */
  tryExitEditMode(): Promise<boolean> {
    if (!this.isEditMode && !this.isAddingEntry) {
      return Promise.resolve(true);
    }

    if (!this.newEntrySaveInFlight && !this.hasUnsavedChanges()) {
      this.isEditMode = false;
      this.cancelAddRow();
      return Promise.resolve(true);
    }

    return this.resolveUnsavedChanges();
  }

  /**
   * Settles whether the caller may move on from unsaved add-row changes, asking Save, Discard or
   * Cancel. While a save is already under way nothing is asked and the caller waits for it: a
   * Discard then would close the row while the request is still created, and a Save would send
   * it twice. Save waits for the save itself, because an approver's save checks for overlaps
   * first and may ask to confirm, so moving on at once would leave that confirmation behind.
   */
  private resolveUnsavedChanges(): Promise<boolean> {
    if (this.newEntrySaveInFlight) return this.newEntrySaveInFlight;

    const dialogData: UnsavedChangesDialogData = {
      entityName: 'Time Off',
      hasValidationErrors: false,
    };
    const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });

    return new Promise((resolve) => {
      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'save') {
          if (this.isAddingEntry) {
            this.saveNewEntry().then(resolve);
          } else {
            resolve(true);
          }
        } else if (result === 'discard') {
          this.discardChanges();
          resolve(true);
        } else {
          // 'cancel' - stay
          resolve(false);
        }
      });
    });
  }

  // --- Inline add row ---

  showAddRow(): void {
    if (this.viewMode !== 'table') {
      this.viewMode = 'table';
    }
    const lookupTypes = this.lookupTypes();
    const defaultType = lookupTypes.find((t) => t.isDefault) ?? lookupTypes[0];
    const defaultTypeId = defaultType?.id ?? 0;
    this.newEntryForm = this.fb.group({
      id: [this.nextId++],
      displayStartDate: [null, Validators.required],
      displayEndDate: [null, Validators.required],
      typeLookupValueId: [defaultTypeId, Validators.required],
    });
    this.setupStartDateListener(this.newEntryForm);
    this.isAddingEntry = true;
  }

  cancelAddRow(): void {
    if (this.newEntryForm) {
      const id = this.newEntryForm.get('id')?.value;
      this.pendingNotes.delete(id);
    }
    this.newEntryForm = null;
    this.isAddingEntry = false;
  }

  /**
   * Saves the add row. Resolves once the row has settled: true when the caller may move on,
   * because the row was saved, the approver discarded it at the overlap confirmation, or there
   * was nothing it could save (no row, or an incomplete one, which the caller leaves behind as
   * before); false when the caller should stay put because the save failed.
   */
  saveNewEntry(): Promise<boolean> {
    // A save already under way is joined rather than repeated: a second run (the unsaved-changes
    // prompt can arrive mid-save) would send the same request twice.
    if (this.newEntrySaveInFlight) return this.newEntrySaveInFlight;
    const employeeId = this.employeeId();
    if (!this.newEntryForm || !employeeId) return Promise.resolve(true);
    if (this.newEntryForm.invalid) return Promise.resolve(true);

    const displayStartDate = this.newEntryForm.get('displayStartDate')?.value;
    const displayEndDate = this.newEntryForm.get('displayEndDate')?.value;
    const typeLookupValueId = this.newEntryForm.get('typeLookupValueId')?.value;
    const tempId = this.newEntryForm.get('id')?.value;

    const request: CreateTimeOffRequestRequest = {
      employeeId: employeeId,
      typeLookupValueId: typeLookupValueId,
      startDate: this.formatDateToISO(displayStartDate),
      endDate: this.formatDateToISO(displayEndDate),
    };

    // An approver's own save is approved on the spot, so the overlap warning the Manage dialog
    // would show a reviewer has to come first, as a confirmation. Anyone else's request starts
    // Pending and gets the warning when an approver opens it.
    const isAdmin = this.apiMode() === 'admin';
    // The row shows its spinner from here until the save settles or the approver discards it.
    this.isNewEntrySaving = true;
    // The request above is what gets checked and saved, so its fields stay put until then: an
    // edit made meanwhile would be checked against nothing and lost when the row closes.
    this.setNewEntryLocked(true);
    const save =
      isAdmin && this.canApprove()
        ? this.checkOverlapThenCreate(employeeId, request, tempId)
        : this.createNewEntry(request, tempId, isAdmin, false);
    this.newEntrySaveInFlight = save.finally(
      () => (this.newEntrySaveInFlight = null),
    );
    return this.newEntrySaveInFlight;
  }

  /**
   * Locks or unlocks the add row's fields while a save is under way. Events stay off: unlocking
   * must not replay the start date into the end date through the start-date listener.
   */
  private setNewEntryLocked(locked: boolean): void {
    if (locked) {
      this.newEntryForm?.disable({ emitEvent: false });
    } else {
      this.newEntryForm?.enable({ emitEvent: false });
    }
  }

  /**
   * The approver's add-row save: checks for overlaps, asks "Approve anyway" when there are any,
   * then creates. Discarding at the confirmation closes the row and resolves true: nothing is
   * left unsaved, so a navigation waiting on this save goes ahead.
   */
  private checkOverlapThenCreate(
    employeeId: string,
    request: CreateTimeOffRequestRequest,
    tempId: number,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      let checkSkipped = false;
      this.timeOffService
        .checkOverlap(employeeId, request.startDate, request.endDate)
        .pipe(
          // The warning is advisory: when the check itself fails the save goes ahead rather than
          // being held hostage to the matter feed, and the success message says it was skipped.
          catchError((err: unknown) => {
            console.error('Time off overlap check failed:', err);
            checkSkipped = true;
            return of(null);
          }),
          switchMap((result) =>
            result?.hasOverlap ? this.confirmOverlappingSave(result) : of(true),
          ),
        )
        .subscribe({
          next: (proceed) => {
            if (proceed) {
              resolve(this.createNewEntry(request, tempId, true, checkSkipped));
            } else {
              // Discard abandons the request: an add row left open with its dates reads as if
              // the time off had been saved, so it closes and nothing remains until the approver
              // adds again. With nothing left to save, a waiting navigation may continue.
              this.isNewEntrySaving = false;
              this.cancelAddRow();
              resolve(true);
            }
          },
          // The confirmation itself failing must not leave the row spinning and locked, or a
          // waiting unsaved-changes prompt hung on a save that will never settle.
          error: (err: unknown) => {
            console.error('Time off overlap confirmation failed:', err);
            this.isNewEntrySaving = false;
            this.setNewEntryLocked(false);
            resolve(false);
          },
        });
    });
  }

  /**
   * "Approve anyway?" for an approver whose new request overlaps a co-assigned resource's time
   * off: one line per overlapping request, the same wording the Manage dialog's callout uses.
   */
  private confirmOverlappingSave(
    result: TimeOffOverlapCheckResultDto,
  ): Observable<boolean> {
    const data: ConfirmationDialogData = {
      title: 'Overlapping time off',
      message: `${this.employeeName() || 'This resource'} shares a matter with resources in the same practice area who are already off on these dates.`,
      sections: [overlapConfirmationSection(result.overlaps)],
      confirmText: 'Approve anyway',
      // Not "Go Back": the row closes and its note goes with it, so the label says so.
      cancelText: 'Discard',
      icon: 'warning',
      iconColor: 'var(--dark-orange-warning)',
      highlightColor: 'var(--state-warning-bg)',
    };
    return this.dialog
      .open(ConfirmationDialogComponent, {
        width: '520px',
        disableClose: true,
        autoFocus: false,
        data,
      })
      .afterClosed()
      .pipe(map((confirmed: boolean | undefined) => !!confirmed));
  }

  /**
   * Sends the new request, then its pending note when one was typed, through the host's route.
   * The caller has already marked the row as saving; this clears it once the save settles and
   * resolves true on success, false on failure. `overlapCheckSkipped` turns the success message
   * into a warning that the request was saved without the overlap check.
   */
  private createNewEntry(
    request: CreateTimeOffRequestRequest,
    tempId: number,
    isAdmin: boolean,
    overlapCheckSkipped: boolean,
  ): Promise<boolean> {
    let createOp: Observable<unknown> = isAdmin
      ? this.timeOffService.create(request)
      : this.timeOffService.createMy(request);

    const pendingNote = this.pendingNotes.get(tempId);
    if (pendingNote) {
      createOp = createOp.pipe(
        switchMap((created) => {
          const dto = created as { id: number };
          return isAdmin
            ? this.notesService.updateTimeOffNotes(dto.id, pendingNote)
            : this.notesService.updateMyTimeOffNotes(dto.id, pendingNote);
        }),
      );
    }

    return new Promise((resolve) => {
      createOp.subscribe({
        next: () => {
          this.pendingNotes.delete(tempId);
          if (overlapCheckSkipped) {
            this.snackbarService.showWarning(
              'Time off saved. The overlap check was unavailable.',
            );
          } else {
            this.snackbarService.showSuccess('Time off saved successfully');
          }
          this.isNewEntrySaving = false;
          this.cancelAddRow();
          // Show the page the saved request belongs on, so it is visible once the
          // host reloads: Upcoming if it has not ended, otherwise its end year.
          // The add form accepts past dates, for recording a missed absence.
          const hasEnded = request.endDate < this.toDateKey(new Date());
          this.setListPage(
            hasEnded ? Number(request.endDate.slice(0, 4)) : 'upcoming',
          );
          this.entriesSaved.emit();
          this.refreshBalanceAfterChange();
          resolve(true);
        },
        error: (err) => {
          console.error('Failed to save time off request:', err);
          this.snackbarService.showError('Failed to save time off request');
          this.isNewEntrySaving = false;
          // The row stays open for another try, so its fields come back.
          this.setNewEntryLocked(false);
          resolve(false);
        },
      });
    });
  }

  getNewEntryHasNotes(): boolean {
    if (!this.newEntryForm) return false;
    const id = this.newEntryForm.get('id')?.value;
    return this.pendingNotes.has(id);
  }

  openNewEntryNotesDialog(): void {
    if (!this.newEntryForm) return;
    const id = this.newEntryForm.get('id')?.value;
    this.openPendingNotesDialog(id);
  }

  // --- Form operations ---

  getDaysOff(group: FormGroup): number {
    const start = group.get('displayStartDate')?.value;
    const end = group.get('displayEndDate')?.value;

    if (!start || !end) return 0;

    const startDate = new Date(start);
    const endDate = new Date(end);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
    if (endDate < startDate) return 0;

    // Count business days (Mon-Fri), excluding configured holidays
    const holidaySet = this.getHolidayDateKeySet();
    const cursor = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
    );
    const last = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
    );

    let count = 0;
    while (cursor <= last) {
      const day = cursor.getDay();
      const isWeekend = day === 0 || day === 6;
      if (!isWeekend && !holidaySet.has(this.toDateKey(cursor))) {
        count++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return count;
  }

  private getHolidayDateKeySet(): Set<string> {
    const set = new Set<string>();
    for (const h of this.holidays) {
      if (!h?.date) continue;
      const [month, day, year] = h.date.split('/');
      if (!month || !day || !year) continue;
      set.add(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
    return set;
  }

  private toDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  getMinEndDate(group: FormGroup): Date | null {
    const displayStartDate = group.get('displayStartDate')?.value;
    if (!displayStartDate) return null;
    return new Date(displayStartDate);
  }

  // --- Unsaved changes ---

  hasUnsavedChanges(): boolean {
    if (this.isAddingEntry && this.newEntryForm) {
      const start = this.newEntryForm.get('displayStartDate')?.value;
      const end = this.newEntryForm.get('displayEndDate')?.value;
      if (start || end || this.pendingNotes.size > 0) return true;
      return false;
    }
    return false;
  }

  /**
   * Check if there are unsaved changes that need to be handled before navigation.
   */
  checkUnsavedChangesForNavigation(): Promise<boolean> {
    if (
      !this.newEntrySaveInFlight &&
      ((!this.isEditMode && !this.isAddingEntry) || !this.hasUnsavedChanges())
    ) {
      return Promise.resolve(true);
    }

    return this.resolveUnsavedChanges();
  }

  // --- Private helpers ---

  private setupStartDateListener(group: FormGroup): void {
    group
      .get('displayStartDate')
      ?.valueChanges.subscribe((displayStartDate: Date | null) => {
        if (displayStartDate) {
          group.get('displayEndDate')?.setValue(new Date(displayStartDate));
        }
      });
  }

  private discardChanges(): void {
    this.pendingNotes.clear();
    this.isEditMode = false;
    this.cancelAddRow();
  }

  private formatDateToISO(date: Date | null): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private resolveTypeName(typeLookupValueId: number): string {
    const lookup = this.lookupTypes().find((t) => t.id === typeLookupValueId);
    return lookup?.name || 'Unknown';
  }

  private resolveTypeLookupId(typeName: string | TimeOffEntry['type']): number {
    const lookup = this.lookupTypes().find((t) => t.name === typeName);
    return lookup?.id || 0;
  }

  // --- Calendar view methods ---

  /**
   * Opens the browser print dialog on the month view (PBI 6471), so the result
   * can be saved as a PDF and emailed on.
   *
   * Prints whatever the calendar is currently showing, filters included, rather
   * than resetting to a canonical month: the point is to share the view the user
   * has already arrived at.
   */
  printCalendar(): void {
    const api = this.calendarComponent()?.getApi();
    if (!api) return;

    this.printedOn = new Date();
    // Mirrors the caption above the calendar, so the saved PDF is named after
    // whose time off it shows.
    const name = this.employeeName();
    openPrintDialog({
      api,
      dayMaxEvents: DEFAULT_DAY_MAX_EVENTS,
      documentTitlePrefix: name ? `${name} - Time Off` : 'Time Off',
    });
  }

  toggleView(mode: 'table' | 'calendar' | 'balance'): void {
    this.viewMode = mode;
    if (mode === 'balance') {
      this.loadDataForSelectedBalanceYear();
    }
  }

  canGoToNextBalanceYear(): boolean {
    return this.selectedBalanceYear < this.maxBalanceYear;
  }

  canGoToPreviousBalanceYear(): boolean {
    return this.selectedBalanceYear > this.minBalanceYear;
  }

  goToPreviousBalanceYear(): void {
    if (!this.canGoToPreviousBalanceYear()) return;
    this.selectedBalanceYear--;
    this.loadDataForSelectedBalanceYear();
  }

  goToNextBalanceYear(): void {
    if (!this.canGoToNextBalanceYear()) return;
    this.selectedBalanceYear++;
    this.loadDataForSelectedBalanceYear();
  }

  /**
   * The current year as it stood when the sheet was last navigated. The
   * history-or-live decision is anchored here rather than on the live clock,
   * so a tab left open over midnight on New Year's Eve keeps the sheet it has
   * until the user navigates; only the arrow bounds follow the clock.
   */
  private balanceYearBasis = new Date().getFullYear();

  /** True when the selected year renders the read-only history view. */
  get isHistoryYear(): boolean {
    return this.selectedBalanceYear < this.balanceYearBasis;
  }

  /**
   * True when the sheet on screen is the server's next-year projection. Read
   * from the balance itself rather than the browser clock: around
   * New Year the two can disagree, and the flag cannot disagree with the
   * numbers it arrived with. The held balance only counts once it is the
   * selected year's: while a reload for another year is in flight the previous
   * year's sheet is still held, and its flag must not caption the new title.
   */
  get isProjectedBalance(): boolean {
    return (
      this.balance?.year === this.selectedBalanceYear &&
      this.balance.isProjected === true
    );
  }

  /**
   * Whether the admin policy edit may be offered for the sheet on screen. The
   * adjustment endpoints accept the server's current year only, so the test is
   * "this is the server's current year", not merely "not projected": an
   * unflagged next-year sheet (a stored row, or a browser ahead of the server)
   * would otherwise offer a button whose save is a 400.
   */
  get canEditSelectedYear(): boolean {
    return (
      !!this.balance &&
      this.balance.year === this.selectedBalanceYear &&
      this.balance.year === this.balance.serverYear
    );
  }

  /**
   * Refreshes the balance after a request changed. On a history year there is
   * nothing live to refresh, and fetching the selected past year would compute
   * a live balance nobody is looking at; the held sheet is dropped instead so
   * the next visit to a live year reloads it.
   */
  private refreshBalanceAfterChange(): void {
    if (!this.balance) return;
    if (this.isHistoryYear) {
      this.balance = null;
      return;
    }
    this.loadBalance();
  }

  /**
   * Loads the data backing the balance view for the selected year: the live
   * balance for the current or the next (projected) year, the finalized
   * history snapshot for a past year. Each is kept while its year stays
   * selected.
   */
  private loadDataForSelectedBalanceYear(): void {
    this.balanceYearBasis = this.effectiveCurrentYear;
    if (this.isHistoryYear) {
      // History is a finalized, immutable snapshot — only fetch when we don't
      // already hold that year's data (mirrors the balance cache guard below).
      if (this.history?.year !== this.selectedBalanceYear) {
        this.loadHistory(this.selectedBalanceYear);
      }
    } else if (this.balance?.year !== this.selectedBalanceYear) {
      // The current and the projected year share one field, so a change of
      // year reloads it.
      this.loadBalance();
    } else {
      // Back on a year already held: any load still in flight is for another
      // year and its response is discarded, so nothing else would clear the
      // spinner it started.
      this.balanceLoading = false;
      this.balanceError = null;
    }
  }

  loadBalance(): void {
    this.balanceLoading = true;
    this.balanceError = null;
    const employeeId = this.employeeId();
    const isSelfService = this.apiMode() === 'self-service';
    // Captured so the callbacks can tell whether the user is still on this
    // year when the response lands.
    const year = this.selectedBalanceYear;

    const balance$ = isSelfService
      ? this.timeOffService.getMyBalance(year)
      : this.timeOffService.getBalance(employeeId, year);

    // Load resource policies alongside the balance so the edit dialog's typePolicies
    // are never stale/empty when the edit button (admin-only) becomes clickable.
    // Self-service doesn't use them, so skip the call there.
    const policies$ =
      isSelfService || !employeeId
        ? of<TimeOffResourcePolicyDto[]>([])
        : this.timeOffService.getResourcePoliciesByEmployee(employeeId);

    forkJoin([balance$, policies$]).subscribe({
      next: ([data, policies]) => {
        // A slow response for a year the user has already stepped away from
        // must not replace the sheet they are looking at now.
        if (year !== this.selectedBalanceYear) return;
        this.serverCurrentYear = data.serverYear;
        this.browserYearAtServerReply = this.currentListYear;
        this.resourcePolicies = policies;
        this.sortBalances(data);
        this.balance = data;
        this.balanceLoading = false;
        this.buildBalanceFootnotes();
      },
      error: () => {
        if (year !== this.selectedBalanceYear) return;
        this.balanceLoading = false;
        // Don't blank out a usable cached view on a background refresh failure — the
        // error branch renders ahead of the data branch and would otherwise mask valid
        // data with no way to recover. Surface a transient error only when there's
        // nothing to show for THIS year: a sheet cached for another year must not
        // sit under this year's title, so it is dropped and reloaded on the way back.
        if (this.balance?.year === year) {
          this.snackbarService.showError('Unable to refresh balance data.');
        } else {
          this.balance = null;
          this.balanceError = 'Unable to load balance data.';
        }
      },
    });
  }

  loadHistory(year: number): void {
    this.historyLoading = true;
    this.historyError = null;
    const request$ =
      this.apiMode() === 'self-service'
        ? this.timeOffService.getMyHistory(year)
        : this.timeOffService.getHistory(this.employeeId(), year);
    request$.subscribe({
      next: (data) => {
        data.types.sort(TimeOffManagementComponent.compareByTypeOrder);
        this.history = data;
        this.historyLoading = false;
      },
      error: () => {
        this.historyError = 'Unable to load history data.';
        this.historyLoading = false;
      },
    });
  }

  private static readonly typeOrder: Record<string, number> = {
    [TimeOffTypeCodes.VACATION]: 0,
    [TimeOffTypeCodes.SICK]: 1,
    [TimeOffTypeCodes.BEREAVEMENT]: 2,
  };

  /** Fixed display order (vacation, sick, bereavement), then alphabetical. */
  private static compareByTypeOrder(
    a: { typeCode: string; typeName: string },
    b: { typeCode: string; typeName: string },
  ): number {
    const orderA = TimeOffManagementComponent.typeOrder[a.typeCode] ?? 99;
    const orderB = TimeOffManagementComponent.typeOrder[b.typeCode] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.typeName.localeCompare(b.typeName);
  }

  private sortBalances(data: TimeOffBalanceDto): void {
    data.balances.sort(TimeOffManagementComponent.compareByTypeOrder);
  }

  // Display helpers for the balance summary view. The wording (including the
  // April 1st carryover expiry) is fixed copy — the API has no policy-text
  // source; the numbers all come from the balance/history endpoints.
  getBalanceTypeLabel(
    b: Pick<TimeOffTypeBalanceDto, 'typeCode' | 'typeName'>,
  ): string {
    if (
      b.typeCode === TimeOffTypeCodes.VACATION ||
      b.typeCode === TimeOffTypeCodes.SICK
    ) {
      return `${b.typeName} Days`;
    }
    return b.typeName;
  }

  /**
   * Color for the numeric values in the balance summary sentence: green while
   * days are available, red once the balance goes negative. Bereavement's
   * "days taken" count stays neutral (the template applies no class there).
   */
  getBalanceValueClass = balanceValueClass;

  private static readonly footnoteMarkers = ['i', 'ii', 'iii', 'iv'];

  // Cached footnote lines, rebuilt via buildBalanceFootnotes() whenever the
  // inputs settle (balance load, resource-policy load). Kept as a field rather
  // than a getter so the template — which reads it from @if, @for, and once per
  // row via getFootnoteMarker() — doesn't rebuild it on every change-detection
  // tick (each rebuild re-filters displayBalances).
  balanceFootnotes: BalanceFootnote[] = [];

  /**
   * Row markers and footnote lines are built in one pass over displayBalances
   * so the (i)/(ii) superscripts always match the footnote keys below the
   * table. Footnotes are split into text/number parts so the modal can apply
   * the bold green/red value styling to the numbers.
   */
  private buildBalanceFootnotes(): void {
    const footnotes: BalanceFootnote[] = [];
    for (const b of this.displayBalances) {
      const footnote = this.getFootnote(b);
      if (!footnote) continue;
      const marker =
        TimeOffManagementComponent.footnoteMarkers[footnotes.length] ??
        `${footnotes.length + 1}`;
      footnotes.push({ ...footnote, typeCode: b.typeCode, marker });
    }
    this.balanceFootnotes = footnotes;
  }

  getFootnoteMarker(b: TimeOffTypeBalanceDto): string | null {
    return (
      this.balanceFootnotes.find((f) => f.typeCode === b.typeCode)?.marker ??
      null
    );
  }

  /**
   * Opens the balance footnote for a row in a modal (replaces the old inline
   * footnotes below the table). Only rows that have a footnote show the marker.
   */
  openFootnoteDialog(b: TimeOffTypeBalanceDto): void {
    const footnote = this.balanceFootnotes.find(
      (f) => f.typeCode === b.typeCode,
    );
    if (!footnote) return;
    const data: TimeOffBalanceInfoDialogData = {
      title: this.getBalanceTypeLabel(b),
      sentences: footnote.sentences,
    };
    // Purely informational — no unsaved state to protect, so allow native ESC and
    // backdrop close (keyboard users can't otherwise dismiss it).
    this.dialog.open(TimeOffBalanceInfoDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      data,
    });
  }

  private getFootnote(
    b: TimeOffTypeBalanceDto,
  ): Pick<BalanceFootnote, 'sentences'> | null {
    const num = (n: number): BalanceFootnotePart => ({
      text: `${n}`,
      value: n,
    });
    const text = (t: string): BalanceFootnotePart => ({ text: t, value: null });
    const bold = (t: string): BalanceFootnotePart => ({
      text: t,
      value: null,
      bold: true,
    });

    // Vacation and sick share the same explanation; only the type word differs.
    // Sentences are broken onto separate lines. Vacation always shows the full
    // breakdown (carryover clause + "must be used by March 31st"), even at 0
    // carryover. Sick only shows those carryover parts when it actually has
    // carried-over days — otherwise we'd imply carryover is possible when the
    // user simply had none (common for sick).
    const isVacation = b.typeCode === TimeOffTypeCodes.VACATION;
    const isSick = b.typeCode === TimeOffTypeCodes.SICK;
    if (!isVacation && !isSick) return null;

    const typeWord = isVacation ? 'vacation' : 'sick';
    // Vacation always includes the carryover text; sick only when > 0.
    const showCarryover = isVacation || b.carryoverDays > 0;

    const firstSentence: BalanceFootnotePart[] = [
      text('You have '),
      num(b.remaining),
      text(' days left out of '),
      num(b.proratedEntitled),
      text(` available ${typeWord} days`),
    ];
    if (showCarryover) {
      // Split the carry-over contribution into its two distinct buckets, because they have
      // different deadlines: genuine carried-over days must be used by March 31 (the April 1 job
      // forfeits the rest), while the LOA credit (vacation only) survives through Dec 31. Combining
      // them under "carried over from last year" would attach the credit to the wrong deadline (the
      // "must be used by March 31st" sentence below). Both are derived from the totals so they
      // always sum with proratedEntitled to totalAvailable, whichever date-aware regime the API
      // applied.
      const credit = b.carryoverCredit;
      const carriedOver = this.carryoverInTotal(b) - credit;
      // On the projected sheet the carry-over is this year's unused days as they
      // stand today, not a finalized figure; say so, and name the year.
      const priorYear = (this.balance?.year ?? this.selectedBalanceYear) - 1;
      firstSentence.push(
        text(' and '),
        num(carriedOver),
        this.isProjectedBalance
          ? text(` days projected to carry over from ${priorYear}`)
          : text(' days carried over from last year'),
      );
      if (credit > 0) {
        firstSentence.push(
          text(', plus a '),
          num(credit),
          text(' day Leave of Absence credit (valid through Dec 31)'),
        );
      }
      // Fold it into a "for a total of N" only when some carry-over/credit actually counts, so the
      // sentence never contradicts the headline number.
      if (this.carryoverInTotal(b) > 0) {
        firstSentence.push(
          text(', for a total of '),
          num(b.totalAvailable),
          text(' days.'),
        );
      } else {
        firstSentence.push(text('.'));
      }
    } else {
      firstSentence.push(text('.'));
    }

    const sentences: BalanceFootnotePart[][] = [
      firstSentence,
      [
        text("You've taken, or have scheduled, "),
        num(b.adjustedDaysTaken),
        text(' approved days off.'),
      ],
    ];
    // The "must be used by March 31st, or they are lost" warning applies to genuine carried-over
    // days. For sick it is only meaningful while the deadline is upcoming: after April 1 the
    // remaining sick carry-over reflects the retained Q1-consumed portion, so the warning would be
    // stale/false (PBI 6228). Vacation keeps the sentence year-round to explain the Leave of Absence
    // exception (the "unless…" clause) — the exception that produces the separately-labeled LOA
    // credit shown above, which carries its own Dec 31 validity rather than the April 1 deadline.
    const sickDeadlinePassed =
      isSick && new Date() >= new Date(this.selectedBalanceYear, 3, 1); // month 3 = April
    if (showCarryover && !sickDeadlinePassed) {
      const expirySentence: BalanceFootnotePart[] = [
        text('Carried over days must be used by '),
        bold('March 31st'),
        text(', or they are lost'),
      ];
      if (isVacation) {
        expirySentence.push(
          text(", unless you've taken a "),
          bold('Leave of Absence'),
        );
      }
      expirySentence.push(text('.'));
      sentences.push(expirySentence);
    }
    // The projection moves with this year's bookings until the Jan 1 job writes
    // the real figure; a resource planning next year should know that.
    if (showCarryover && this.isProjectedBalance) {
      sentences.push([
        text('Carry-over is finalized on '),
        bold('January 1'),
        text(' and may change as this year’s time off changes.'),
      ]);
    }
    // Self-service users never see the admin policy dialog, so this is the one place they learn
    // why their entitlement differs from an office colleague's. Vacation only: it is the only type
    // a practice area can override.
    if (isVacation && b.practiceAreaCode) {
      sentences.push([
        text('Your allotment follows your practice area’s '),
        bold('Vacation'),
        text(' policy rather than the office default.'),
      ]);
    }
    return { sentences };
  }

  /**
   * The carry-over amount actually folded into `totalAvailable` for the "(entitled + carry-over)"
   * breakdown. The server's rule is date-aware: the full carried amount counts until April 1 of the
   * balance year (the days are still usable in Q1), and only the realized (Q1-consumed) portion —
   * plus vacation's window-exempt LOA credit — counts after (Task 6758, extended to vacation).
   * Derived from the totals so it always sums with `proratedEntitled` to `totalAvailable`,
   * whichever regime and mix of carry-over/credit the API applied.
   */
  carryoverInTotal(b: TimeOffTypeBalanceDto): number {
    return b.totalAvailable - b.proratedEntitled;
  }

  get displayBalances(): TimeOffTypeBalanceDto[] {
    if (!this.balance) return [];
    return this.filterVisibleTypes(
      this.balance.balances,
      (b) =>
        b.totalAvailable !== 0 ||
        b.adjustedDaysTaken !== 0 ||
        b.remaining !== 0,
    );
  }

  /** History rows for a past year, filtered by the same visibility rules as balances. */
  get displayHistoryTypes(): TimeOffTypeHistoryDto[] {
    if (!this.history) return [];
    return this.filterVisibleTypes(
      this.history.types,
      (h) => h.entitled !== 0 || h.taken !== 0 || h.remaining !== 0,
    );
  }

  /**
   * Shared visibility rule for balance and history rows: admins see everything; otherwise
   * self-service hides sick/bereavement, and bereavement is hidden unless it has activity
   * (per the type-specific `hasActivity` predicate). Keeps the two views' type sets in sync.
   */
  private filterVisibleTypes<T extends { typeCode: string }>(
    rows: T[],
    hasActivity: (row: T) => boolean,
  ): T[] {
    if (this.canEditEntitledDays()) return rows;
    return rows.filter((row) => {
      if (this.apiMode() === 'self-service') {
        if (row.typeCode === TimeOffTypeCodes.SICK) return false;
        if (row.typeCode === TimeOffTypeCodes.BEREAVEMENT) return false;
      }
      if (row.typeCode !== TimeOffTypeCodes.BEREAVEMENT) return true;
      return hasActivity(row);
    });
  }

  get canAddEntry(): boolean {
    return !this.isReadOnly();
  }

  // --- Balance edit ---

  /**
   * Open the policy edit dialog (Entitled, Carried Over, Notes) for the
   * given balance row. Reloads the balance on save.
   */
  openPolicyEditDialog(b: TimeOffTypeBalanceDto): void {
    const employeeId = this.employeeId();
    if (!employeeId) return;

    const typeLookupValueId = this.resolveTypeLookupIdByCode(b.typeCode);
    if (!typeLookupValueId) return;

    // With effective-year policies an employee can have several rows per type
    // (baseline + dated); the dialog resolves which one applies per year.
    const typePolicies = this.resourcePolicies.filter(
      (p) => p.typeCode === b.typeCode,
    );

    const data: TimeOffPolicyEditDialogData = {
      employeeId,
      employeeName: this.employeeName(),
      typeCode: b.typeCode,
      typeName: b.typeName,
      typeLookupValueId,
      defaultEntitled: b.defaultEntitled,
      proratedEntitled: b.proratedEntitled,
      totalAvailable: b.totalAvailable,
      officeDefaultEntitledDays: b.officeDefaultEntitledDays,
      practiceAreaCode: b.practiceAreaCode ?? null,
      officeCode: this.balance?.officeCode ?? null,
      // The year the server computed this balance for. The dialog labels and writes it rather than
      // deriving a year from the browser clock, which can differ from the server's around New Year.
      year: this.balance?.year,
      typePolicies,
    };

    const dialogRef = this.dialog.open(TimeOffPolicyEditDialogComponent, {
      width: '720px',
      maxHeight: '90vh',
      autoFocus: false,
      // The dialog guards backdrop/ESC itself (prompts on unsaved changes), so
      // block Material's default close and let the dialog route those events.
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: TimeOffPolicyEditDialogResult | null) => {
        if (result?.saved) {
          this.balance = null;
          this.loadBalance();
          this.entitledDaysSaved.emit();
        }
      });
  }

  private resolveTypeLookupIdByCode(typeCode: string): number | null {
    const lookup = this.lookupTypes().find((t) => t.code === typeCode);
    return lookup?.id ?? null;
  }

  /**
   * Build FullCalendar events from TimeOffEntry data.
   * Delegates filtering (DENIED/CANCELLED) and mapping to the shared utility.
   */
  private buildCalendarEvents(): void {
    const timeOffEvents = mapTimeOffEntriesToEventInputs(
      this.entries(),
      this.employeeId(),
      this.employeeName(),
      this.lookupTypes(),
      this.classYear(),
      this.seniority(),
    );
    const holidayEvents = mapHolidaysToEventInputs(this.holidays);
    this.calendarOptions = {
      ...this.calendarOptions,
      events: [...timeOffEvents, ...holidayEvents],
    };
  }

  /**
   * FullCalendar puts native `title` tooltips (OS-styled) on the prev/next
   * buttons. Strip them so no tooltip shows on hover, preserving the text as an
   * aria-label for screen readers.
   */
  private styleCalendarNavTooltips(): void {
    const root = this.elementRef.nativeElement as HTMLElement;
    const buttons = root.querySelectorAll<HTMLElement>(
      '.fc-prev-button, .fc-next-button',
    );
    buttons.forEach((btn) => {
      const title = btn.getAttribute('title');
      if (!title) return;
      btn.setAttribute('aria-label', title);
      btn.removeAttribute('title');
    });
  }

  // --- Approve / Deny actions ---

  approveEntry(entry: TimeOffEntry, note?: string): void {
    this.timeOffService.updateStatus(entry.id, 'APPROVED').subscribe({
      next: () => {
        if (note) {
          this.timeOffService
            .saveManagerNote(entry.id, note, 'APPROVED')
            .subscribe();
        }
        this.snackbarService.showSuccess('Time off request approved.');
        this.requestStatusChanged.emit();
        this.refreshBalanceAfterChange();
      },
      error: () => {
        this.snackbarService.showError('Failed to approve time off request.');
      },
    });
  }

  denyEntry(entry: TimeOffEntry, note?: string): void {
    this.timeOffService.updateStatus(entry.id, 'DENIED').subscribe({
      next: () => {
        if (note) {
          this.timeOffService
            .saveManagerNote(entry.id, note, 'DENIED')
            .subscribe();
        }
        this.snackbarService.showSuccess('Time off request returned.');
        this.requestStatusChanged.emit();
        this.refreshBalanceAfterChange();
      },
      error: () => {
        this.snackbarService.showError('Failed to return time off request.');
      },
    });
  }

  /**
   * The service picks the route from ownership and status and saves the note where a note is
   * taken; this side only knows whose request it is. Self-service pages always show the
   * signed-in user's own requests; the admin view of a resource page can be your own page too.
   */
  cancelEntry(entry: TimeOffEntry, note?: string): void {
    const isOwn =
      this.apiMode() === 'self-service' ||
      this.authService.isCurrentEmployee(this.employeeId());
    this.timeOffService
      .cancelRequestFor(entry.id, { isOwn, statusCode: entry.statusCode }, note)
      .subscribe({
        next: () => {
          this.snackbarService.showSuccess('Time off request cancelled.');
          this.requestStatusChanged.emit();
          this.refreshBalanceAfterChange();
        },
        error: () => {
          this.snackbarService.showError('Failed to cancel time off request.');
        },
      });
  }
}
